/** @jsx jsx */
/* eslint-disable jira/react/no-style-attribute */
import { type MouseEventHandler, useCallback, useRef } from 'react';
import { css, jsx } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { layers } from '@atlassian/jira-common-styles/src/main.tsx';
import { useCell } from '../../controllers/cell/index.tsx';
import { useScrollX } from '../../controllers/container/index.tsx';
import { useGridActions, useCellWidth, useResizerVisible } from '../../controllers/grid/index.tsx';
import { useResize } from '../../controllers/resize/index.tsx';

interface Props {
	label: string;
	className?: string;
	height?: number | string;
	testId?: string;
}

export const ResizeHandle = ({ height, className, testId, label }: Props) => {
	const [resizePayload, { hover, activate }] = useResize();
	const [{ column, colSpan }] = useCell();
	const [scrollX] = useScrollX();
	const [visible] = useResizerVisible({ column, scrollX, preview: true });
	const columnToResize = column + (colSpan - 1);
	const { resize } = useGridActions();
	const hovered = resizePayload.column === columnToResize && resizePayload.hovered;
	const active = resizePayload.column === columnToResize && resizePayload.active;

	const handleMouseMove: MouseEventHandler = useCallback(() => {
		if (!hovered) {
			hover(columnToResize, true);
		}
	}, [columnToResize, hover, hovered]);

	const handleMouseLeave: MouseEventHandler = useCallback(() => {
		hover(columnToResize, false);
	}, [columnToResize, hover]);

	const handleMouseDown: MouseEventHandler = useCallback(
		(e) => {
			e.preventDefault(); // Such as drag behavior being handled on ancestors
			activate(columnToResize, true);

			/** The blanket element to ensure that:
			 *
			 * - the mouse cursor can't interact with other elements while dragging, and
			 * - the mouse cursor is always ew-resize while dragging regardless the handle position.
			 * */

			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			const blanketEl = document.createElement('div');

			blanketEl.style.zIndex = String(layers.blanket);
			blanketEl.style.position = 'fixed';
			blanketEl.style.inset = '0px';
			blanketEl.style.cursor = 'ew-resize';

			let delta = 0;
			const anchorOffset = e.clientX;

			// eslint-disable-next-line @typescript-eslint/no-shadow
			const handleMouseMove = (e: MouseEvent) => {
				delta = e.clientX - anchorOffset;
				resize(columnToResize, delta);
			};

			const handleMouseUp = () => {
				activate(columnToResize, false);
				resize(columnToResize, delta, true);

				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				document.body.removeChild(blanketEl);

				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				document.removeEventListener('mousemove', handleMouseMove);

				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				document.removeEventListener('mouseup', handleMouseUp);
			};

			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			document.body.appendChild(blanketEl);

			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			document.addEventListener('mousemove', handleMouseMove);

			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			document.addEventListener('mouseup', handleMouseUp);
		},
		[activate, columnToResize, resize],
	);

	const handleInputFocus = useCallback(() => {
		activate(columnToResize, true);
	}, [activate, columnToResize]);

	const handleInputBlur = useCallback(() => {
		activate(columnToResize, false);
	}, [activate, columnToResize]);

	const handleResize = useCallback(
		(delta: number, commit?: boolean) => {
			resize(columnToResize, delta, commit);
		},
		[resize, columnToResize],
	);

	return (
		<div
			role="presentation"
			onMouseMove={handleMouseMove}
			onMouseLeave={handleMouseLeave}
			onMouseDown={handleMouseDown}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
			className={className}
			css={[handleStyles, (hovered || active) && activeStyles, visible && visibleStyles]}
			style={{ height }}
			data-testid={testId}
		>
			<ResizeInput
				onFocus={handleInputFocus}
				onBlur={handleInputBlur}
				onResize={handleResize}
				label={label}
			/>
		</div>
	);
};

const ResizeInput = ({
	label,
	onFocus,
	onBlur,
	onResize,
}: Pick<Props, 'label'> & {
	onFocus: () => void;
	onBlur: () => void;
	onResize: (delta: number, commit?: boolean) => void;
}) => {
	const [{ column, colSpan }] = useCell();
	const { setFocusVisible } = useGridActions();
	const [cellWidth] = useCellWidth({ column, colspan: colSpan, preview: false });
	const [cellWidthPreview] = useCellWidth({ column, colspan: colSpan, preview: true });

	const cellWidthPreviewRef = useRef(cellWidthPreview);
	cellWidthPreviewRef.current = cellWidthPreview;

	const deltaRef = useRef(0);
	deltaRef.current = cellWidthPreview - cellWidth;

	const handleFocus = useCallback(() => {
		deltaRef.current = 0;
		onFocus();
	}, [onFocus]);

	const handleBlur = useCallback(() => {
		onResize(deltaRef.current, true);
		onBlur();
	}, [onResize, onBlur]);

	const handleChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const adjustment = parseInt(e.target.value, 10) < cellWidthPreviewRef.current ? -10 : 10;
			onResize(deltaRef.current + adjustment);
		},
		[onResize],
	);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLInputElement>) => {
			if (e.key === 'Enter') {
				e.preventDefault();
				setFocusVisible(true);
			}
		},
		[setFocusVisible],
	);

	return (
		<input
			aria-label={label}
			css={rangeInputStyles}
			type="range"
			min={0}
			max={1e6} // Some large number simulating infinity
			step={1}
			onFocus={handleFocus}
			onBlur={handleBlur}
			value={cellWidthPreview}
			onChange={handleChange}
			onKeyDown={handleKeyDown}
		/>
	);
};

const handleStyles = css({
	width: '2px',
	position: 'relative',
	cursor: 'ew-resize',
	visibility: 'hidden',

	'&::before': {
		content: '""',
		position: 'absolute',
		inset: token('space.0'),
		left: token('space.negative.050'),
		right: token('space.negative.050'),
	},
});

const activeStyles = css({
	backgroundColor: token('color.background.selected.bold'),
});

const visibleStyles = css({
	visibility: 'visible',
});

const rangeInputStyles = css({
	width: 0,
	height: 0,
	clip: 'rect(0 0 0 0)',
	overflow: 'hidden',
});
